import { initI18n, Resource, i18n } from '@sunbit-dev/translations';
import { AddResources, Init, LoadResources, Namespaces, Status } from './models';
import deepMerge from 'deepmerge';

export class TranslationsService {
  private status: Status = Status.uninitialized;

  private initializationPromise: Promise<void> | undefined;

  constructor({ debug }: Init) {
    const i18Instance = initI18n({
      debug,
      getResources: this.loadResources,
    });

    this.initializationPromise = i18Instance
      .then(() => {
        this.status = Status.initialized;
      })
      .catch((err) => {
        this.status = Status.failed;
        console.error(err);
      });
  }

  get resources() {
    return i18n.services.resourceStore.data;
  }

  get language() {
    return i18n.language;
  }

  onInitialized = () => this.initializationPromise;

  loadResources = async ({ language }: LoadResources): Promise<{ namespaces: Namespaces; resources: Resource }> => {
    const applicationTextsPromise = import(`~/i18n/locales/${language}/translation.json`);
    const purchaseCommonTextsPromise = import(
      `@sunbit-dev/purchase-common/src/i18n/locales/${language}/translation.json`
    );

    const [applicationTexts, purchaseCommonTexts] = await Promise.all([
      applicationTextsPromise,
      purchaseCommonTextsPromise,
    ]);

    const resources = {
      [language]: deepMerge(purchaseCommonTexts, applicationTexts, {
        arrayMerge: (destinationArray, sourceArray, ...args) => sourceArray,
      }),
    } as Resource;

    return { resources: resources, namespaces: Object.keys(resources[language]) };
  };

  addResource = async ({ name, type }: AddResources) => {
    try {
      const { default: resource } = await import(`~/i18n/locales/${this.language}/${type}/${name?.toLowerCase()}.json`);

      Object.entries(resource).forEach(([namespace, value]) => {
        i18n.addResourceBundle(this.language, namespace, value, true, true);
      });

      // In order to trigger react re-render
      i18n.changeLanguage(this.language);

      console.log(`translation resource loaded type: ${type} name: ${name}`);
    } catch (error: any) {
      console.warn('Had error fetching translation resource', error);
    }
  };
}
